import {
  File as FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  LucideIcon,
  UploadIcon,
  X,
} from 'lucide-react'
import { useFormatter, useTranslations } from 'next-intl'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { TruncatedFileNameDisplay } from '@/components/common/truncated-file-name-display'
import DashboardLoadingIcon from '@/components/icon/dashboard-loading-icon'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import {
  BYTES_PER_MB,
  FEEDBACK_ATTACHMENT_ALLOWED_FORMATS,
  FEEDBACK_ATTACHMENT_ALLOWED_MIME_TYPES,
  FEEDBACK_ATTACHMENT_MAX_FILES,
  FEEDBACK_ATTACHMENT_MAX_FILE_SIZE_MB,
  FEEDBACK_ATTACHMENT_MAX_TOTAL_SIZE_MB,
  FeedbackAttachmentRejection,
  feedbackFileKey,
  feedbackPreviewKind,
  getFeedbackFileExtension,
  screenFeedbackAttachments,
  totalFeedbackAttachmentBytes,
} from '@/lib/feedback-attachments'
import { cn } from '@/lib/utils'

import { FeedbackAttachmentPreview, FeedbackAttachmentPreviewDialog } from './feedback-attachment-preview-dialog'

interface FeedbackAttachmentUploaderProps {
  files: File[]
  onFilesChange: (_files: File[]) => void
  disabled?: boolean
  loading?: boolean
}

// Only what the rejection list renders, so rejected files (e.g. an oversized video) are not held
interface Rejection {
  key: string
  name: string
  reason: FeedbackAttachmentRejection['reason']
}

// 0 -> "0", 0.003 -> "<0.1", 6 -> "6", 10.46 -> "10.5" ("10,5" in vi). Small screenshots would
// otherwise round to "0 MB" and read as if nothing was attached.
const formatMb = (bytes: number, format: ReturnType<typeof useFormatter>) => {
  const mb = bytes / BYTES_PER_MB
  if (mb > 0 && mb < 0.1) return `<${format.number(0.1)}`
  return format.number(mb, { maximumFractionDigits: 1 })
}

// Types with no in-browser renderer (HEIC/HEIF, Office) show an icon instead of a thumbnail

const FALLBACK_ICONS: Record<string, LucideIcon> = {
  '.heic': FileImage,
  '.heif': FileImage,
  '.mp4': FileVideo,
  '.mov': FileVideo,
  '.webm': FileVideo,
  '.pdf': FileText,
  '.docx': FileText,
  '.pptx': FileText,
  '.xlsx': FileSpreadsheet,
}

const iconForFile = (file: File) => FALLBACK_ICONS[getFeedbackFileExtension(file)] ?? FileIcon

export const FeedbackAttachmentUploader: FC<FeedbackAttachmentUploaderProps> = ({
  files,
  onFilesChange,
  disabled = false,
  loading = false,
}) => {
  const t = useTranslations('Feedback')
  const format = useFormatter()
  const uploadRef = useRef<HTMLInputElement>(null)
  const dragCounter = useRef(0)
  const [dragging, setDragging] = useState(false)
  const [objectUrls, setObjectUrls] = useState<Record<string, string>>({})
  const [brokenThumbnails, setBrokenThumbnails] = useState<string[]>([])
  const [rejections, setRejections] = useState<Rejection[]>([])
  const [preview, setPreview] = useState<FeedbackAttachmentPreview | null>(null)
  const objectUrlsRef = useRef(new Map<string, string>())

  // Screening is async (it reads each file's first bytes), so two quick drops could otherwise both
  // screen against the same stale selection. Queue them and always screen against the latest files.
  const filesRef = useRef(files)
  filesRef.current = files
  const screeningQueue = useRef<Promise<void>>(Promise.resolve())

  // `loading` implies non-interactive, so callers only need to pass one of the two
  const isBusy = disabled || loading

  // One object URL per previewable file, shared by its thumbnail and the preview dialog. Only files
  // that were added get a URL and only removed ones are revoked, so adding a file does not re-decode
  // every image already selected.
  useEffect(() => {
    const urls = objectUrlsRef.current
    const keys = new Set(files.map(feedbackFileKey))
    urls.forEach((url, key) => {
      if (keys.has(key)) return
      URL.revokeObjectURL(url)
      urls.delete(key)
    })
    files.forEach((file) => {
      const key = feedbackFileKey(file)
      if (!urls.has(key) && feedbackPreviewKind(file) !== 'none') urls.set(key, URL.createObjectURL(file))
    })
    setObjectUrls(Object.fromEntries(urls))
  }, [files])

  // Release whatever is left when the uploader unmounts (dialog closed)
  useEffect(() => {
    const urls = objectUrlsRef.current
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url))
      urls.clear()
    }
  }, [])

  // Both extensions and MIME types: desktop file pickers filter on the former, iOS Safari and
  // some Android pickers only honour the latter.
  const acceptedTypes = useMemo(
    () => [...FEEDBACK_ATTACHMENT_ALLOWED_FORMATS, ...FEEDBACK_ATTACHMENT_ALLOWED_MIME_TYPES].join(','),
    [],
  )

  // Accumulate onto the current selection so multi-pick across clicks works. Every file is screened
  // here, at pick/drop time: drag and drop bypasses the input's `accept` filter, and the submit-time
  // zod schema alone would let an invalid file sit in the list looking accepted.
  const addFiles = useCallback(
    (incoming: File[]) => {
      if (!incoming.length) return
      screeningQueue.current = screeningQueue.current
        .then(async () => {
          const { accepted, rejected } = await screenFeedbackAttachments(filesRef.current, incoming)
          setRejections(rejected.map(({ file, reason }) => ({ key: feedbackFileKey(file), name: file.name, reason })))
          if (accepted.length) {
            const merged = [...filesRef.current, ...accepted]
            filesRef.current = merged
            onFilesChange(merged)
          }
        })
        // one failed step must not stall every later drop queued behind it
        .catch(() => undefined)
    },
    [onFilesChange],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files ? Array.from(event.target.files) : []
      addFiles(selected)
      // reset so re-selecting the same file (after removal) still fires onChange
      if (uploadRef.current) uploadRef.current.value = ''
    },
    [addFiles],
  )

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    if (isBusy) return
    dragCounter.current++
    setDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    if (isBusy) return
    dragCounter.current--
    if (dragCounter.current === 0) setDragging(false)
  }

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragCounter.current = 0
      setDragging(false)
      // guard in addition to pointer-events-none, in case a drag started before the busy state
      if (isBusy) return
      addFiles(Array.from(e.dataTransfer.files))
    },
    [addFiles, isBusy],
  )

  const handleRemove = useCallback(
    (index: number) => {
      setRejections([])
      onFilesChange(files.filter((_, i) => i !== index))
    },
    [files, onFilesChange],
  )

  const rejectionMessage = ({ name, reason }: Rejection) =>
    t(`rejected.${reason}`, {
      name,
      size: FEEDBACK_ATTACHMENT_MAX_FILE_SIZE_MB,
      count: FEEDBACK_ATTACHMENT_MAX_FILES,
      total: FEEDBACK_ATTACHMENT_MAX_TOTAL_SIZE_MB,
    })

  return (
    <div className="flex flex-col gap-3">
      <div
        role="button"
        tabIndex={0}
        aria-label={loading ? t('uploading') : t('attach')}
        aria-busy={loading}
        onClick={() => {
          if (isBusy) return
          uploadRef.current?.click()
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'flex h-[120px] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-base-border p-6 text-center transition-all duration-200 ease-in-out',
          dragging && 'border-primary bg-primary/5',
          isBusy && 'pointer-events-none',
          disabled && !loading && 'opacity-70',
        )}
      >
        {loading ? (
          <>
            <DashboardLoadingIcon className="size-4" />
            <p className="text-sm font-normal text-muted-foreground">{t('uploading')}</p>
          </>
        ) : (
          <>
            <UploadIcon className={cn('size-4 stroke-muted-foreground', { 'stroke-primary': dragging })} />
            <p className="text-sm font-normal text-muted-foreground">{t('attachHint')}</p>
          </>
        )}
      </div>

      <Input
        ref={uploadRef}
        type="file"
        accept={acceptedTypes}
        multiple
        onChange={handleInputChange}
        className="hidden"
        disabled={isBusy}
        aria-label={t('attach')}
      />

      <p className="text-xs font-normal text-muted-foreground" aria-live="polite">
        {t('counter', {
          count: files.length,
          maxCount: FEEDBACK_ATTACHMENT_MAX_FILES,
          size: formatMb(totalFeedbackAttachmentBytes(files), format),
          maxSize: FEEDBACK_ATTACHMENT_MAX_TOTAL_SIZE_MB,
        })}
      </p>

      {rejections.length > 0 && (
        <ul role="alert" className="flex flex-col gap-1 text-sm font-medium text-destructive">
          {rejections.map((rejection) => (
            // break-words, not break-all: only an unbroken file name may split, never a translated word
            <li key={rejection.key} className="break-words">
              {rejectionMessage(rejection)}
            </li>
          ))}
        </ul>
      )}

      {files.length > 0 && (
        <div className={cn('flex flex-wrap items-start gap-2', loading && 'opacity-70')}>
          {files.map((file, index) => {
            const key = feedbackFileKey(file)
            const url = objectUrls[key]
            const thumbnailUrl =
              feedbackPreviewKind(file) === 'image' && !brokenThumbnails.includes(key) ? url : undefined
            const Icon = iconForFile(file)

            return (
              <div
                key={key}
                className="flex max-w-[200px] flex-row items-center gap-2 overflow-hidden rounded-lg border-DEFAULT border-solid border-base-border bg-base-card p-1.5 text-[13px] shadow-button"
                title={file.name}
              >
                <button
                  type="button"
                  className="flex min-w-0 flex-1 flex-row items-center gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default"
                  onClick={() => setPreview({ file, url })}
                  disabled={isBusy}
                  aria-label={t('preview', { name: file.name })}
                >
                  {thumbnailUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element -- next/image cannot take a blob: URL
                    <img
                      src={thumbnailUrl}
                      alt={file.name}
                      className="size-8 shrink-0 rounded object-cover"
                      // a corrupt or undecodable file drops back to the icon rather than a broken image
                      onError={() => setBrokenThumbnails((current) => [...current, key])}
                    />
                  ) : (
                    <span className="flex size-8 shrink-0 items-center justify-center rounded bg-muted">
                      <Icon className="size-4 text-muted-foreground" />
                    </span>
                  )}
                  <TruncatedFileNameDisplay
                    fileName={file.name}
                    className="select-none text-sm font-normal text-base-card-foreground"
                  />
                </button>
                <Button
                  type="button"
                  className="h-[initial] shrink-0 bg-transparent p-0 text-current hover:bg-transparent"
                  onClick={() => handleRemove(index)}
                  disabled={isBusy}
                  aria-label={t('removeFile')}
                >
                  <X className="h-4 w-4 text-base-card-foreground" />
                </Button>
              </div>
            )
          })}
        </div>
      )}

      <FeedbackAttachmentPreviewDialog preview={preview} onClose={() => setPreview(null)} />
    </div>
  )
}

FeedbackAttachmentUploader.displayName = 'FeedbackAttachmentUploader'
